import { useCallback } from 'react';
import confetti from 'canvas-confetti';

type MilestoneType = '50_percent' | 'all_complete';

export function useConfetti() {
  const triggerMilestone = useCallback((type: MilestoneType) => {
    // Respect prefers-reduced-motion
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const colors = ['#DD5BEA', '#7c3aed', '#ffffff', '#f472b6'];

    if (type === '50_percent') {
      // Brief burst for halfway milestone
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 },
        colors,
        disableForReducedMotion: true,
      });
    } else if (type === 'all_complete') {
      // Larger celebration for full completion
      // First burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors,
        disableForReducedMotion: true,
      });

      // Second burst slightly delayed
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors,
          disableForReducedMotion: true,
        });
      }, 200);

      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors,
          disableForReducedMotion: true,
        });
      }, 400);
    }
  }, []);

  const triggerTaskComplete = useCallback(() => {
    // Small burst for single task completion
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    confetti({
      particleCount: 30,
      spread: 40,
      origin: { y: 0.7 },
      colors: ['#22c55e', '#4ade80', '#86efac'],
      disableForReducedMotion: true,
    });
  }, []);

  const triggerShowcase = useCallback((origin: { x: number; y: number }) => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    confetti({
      particleCount: 35,
      spread: 50,
      origin,
      colors: ['#7b5cff', '#a78bfa', '#FFD700', '#ffffff'],
      ticks: 80,
      disableForReducedMotion: true,
    });
  }, []);

  const triggerEliteCelebration = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const eliteColors = ['#FFD700', '#8B5CF6', '#ffffff'];
    const duration = 3000;
    const end = Date.now() + duration;

    // Initial big burst from center
    confetti({
      particleCount: 120,
      spread: 100,
      origin: { y: 0.3 },
      colors: eliteColors,
      disableForReducedMotion: true,
    });

    // Continuous rain from top
    const frame = () => {
      if (Date.now() > end) return;

      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0 },
        colors: eliteColors,
        disableForReducedMotion: true,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0 },
        colors: eliteColors,
        disableForReducedMotion: true,
      });

      requestAnimationFrame(frame);
    };

    // Side bursts staggered
    setTimeout(() => {
      confetti({
        particleCount: 60,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.5 },
        colors: eliteColors,
        disableForReducedMotion: true,
      });
    }, 300);

    setTimeout(() => {
      confetti({
        particleCount: 60,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.5 },
        colors: eliteColors,
        disableForReducedMotion: true,
      });
    }, 600);

    // Second center burst
    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 90,
        origin: { y: 0.4 },
        colors: eliteColors,
        disableForReducedMotion: true,
      });
    }, 1000);

    requestAnimationFrame(frame);
  }, []);

  return {
    triggerMilestone,
    triggerTaskComplete,
    triggerShowcase,
    triggerEliteCelebration,
  };
}
